import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthWidgetComponent } from '../widgets/auth/auth-widget.component';
import { ApiClientService } from '../core/api-client.service';
import { AuthService } from '../core/auth.service';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { CallHistoryWidgetComponent } from '../widgets/live-call/call-history-widget.component';
import { formatLocalDate } from '../core/local-date.util';
import { SiteConfigService } from '../core/site-config.service';
import { FavoriteCollectionService, UserCollection } from '../widgets/collections/favorite-collection.service';

type AccountSection = 'profile' | 'contact' | 'password' | 'orders' | 'calls' | 'reviews' | 'history' | 'following' | 'collections';

interface AccountNavItem {
  key: AccountSection;
  title: string;
  description: string;
  routeKey: string;
  group: string;
}

interface AccountProfile {
  id: number;
  userName: string | null;
  name: string | null;
  midName: string | null;
  surname: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phone: string | null;
  phoneCountry: string | null;
}

interface AccountListItem {
  id?: number;
  listingID?: number;
  sellerID?: number;
  rating?: number;
  title?: string;
  comment?: string;
  listingTitle?: string;
  listingSlug?: string;
  slug?: string;
  sellerName?: string;
  shopName?: string;
  userName?: string;
  imageUrl?: string;
  createdAt?: string;
  followedAt?: string;
}

interface ApiResponse<T> {
  success?: boolean;
  result?: boolean;
  message?: string;
  data?: T;
}

@Component({
  selector: 'app-account-page',
  imports: [AuthWidgetComponent, CallHistoryWidgetComponent, FormsModule, RouterLink, RouterLinkActive],
  template: `
    <section class="account-page">
      <header class="page-title">
        <p class="eyebrow">{{ i18n.t('myAccount') }}</p>
        <h1>{{ activeItem().title }}</h1>
        <p>{{ activeItem().description }}</p>
      </header>

      @if (!auth.loggedIn()) {
        <div class="account-login-card">
          <span class="soft-mark">FD</span>
          <div>
            <h2>{{ i18n.t('loginRequired') }}</h2>
            <p>{{ i18n.t('accountLoginLead') }}</p>
          </div>
          <app-auth-widget />
        </div>
      } @else {
        <div class="account-shell">
          <aside class="account-sidebar" aria-label="Hesap menüsü">
            <div class="user-card">
              <span>{{ initials() }}</span>
              <strong>{{ displayName() }}</strong>
              <small>{{ profile()?.email || auth.user()?.email }}</small>
            </div>

            @for (group of groupedNav(); track group.name) {
              <div class="nav-group">
                <p>{{ group.name }}</p>
                @for (item of group.items; track item.key) {
                  <a
                    [routerLink]="i18n.routePath(item.routeKey)"
                    routerLinkActive="active"
                    [routerLinkActiveOptions]="{ exact: true }"
                  >
                    <span>{{ item.title }}</span>
                    <i>›</i>
                  </a>
                }
              </div>
            }
          </aside>

          <article class="account-content">
            @if (notice()) {
              <p class="notice success">{{ notice() }}</p>
            }
            @if (error()) {
              <p class="notice error">{{ error() }}</p>
            }

            @if (loading()) {
              <div class="account-skeleton">
                <i></i><i></i><i></i>
              </div>
            } @else if (section() === 'profile') {
              <form class="account-form" (ngSubmit)="saveProfile()">
                <label>
                  <span>{{ i18n.t('name') }}</span>
                  <input [(ngModel)]="personalForm.name" name="name" autocomplete="given-name" required />
                </label>
                <label>
                  <span>{{ i18n.t('secondName') }}</span>
                  <input [(ngModel)]="personalForm.midName" name="midName" autocomplete="additional-name" />
                </label>
                <label>
                  <span>{{ i18n.t('surname') }}</span>
                  <input [(ngModel)]="personalForm.surname" name="surname" autocomplete="family-name" />
                </label>
                <label>
                  <span>{{ i18n.t('gender') }}</span>
                  <select [(ngModel)]="personalForm.sex" name="sex">
                    <option value="">{{ i18n.t('other') }}</option>
                    <option value="male">{{ i18n.t('male') }}</option>
                    <option value="female">{{ i18n.t('female') }}</option>
                    <option value="other">{{ i18n.t('other') }}</option>
                  </select>
                </label>
                <label>
                  <span>{{ i18n.t('birthDate') }}</span>
                  <input [(ngModel)]="personalForm.dateOfBirth" name="dateOfBirth" type="date" autocomplete="bday" />
                </label>
                <button type="submit" [disabled]="saving()">{{ i18n.t('saveChanges') }}</button>
              </form>
            } @else if (section() === 'contact') {
              <form class="account-form" (ngSubmit)="saveContact()">
                <label>
                  <span>{{ i18n.t('email') }}</span>
                  <input [(ngModel)]="contactForm.email" name="email" type="email" autocomplete="email" required />
                </label>
                <label>
                  <span>{{ i18n.t('phoneCountry') }}</span>
                  <input [(ngModel)]="contactForm.phoneCountry" name="phoneCountry" autocomplete="country" />
                </label>
                <label>
                  <span>{{ i18n.t('phone') }}</span>
                  <input [(ngModel)]="contactForm.phone" name="phone" type="tel" autocomplete="tel" />
                </label>
                <button type="submit" [disabled]="saving()">{{ i18n.t('saveChanges') }}</button>
              </form>
            } @else if (section() === 'password') {
              <form class="account-form">
                <label>
                  <span>{{ i18n.t('currentPassword') }}</span>
                  <input name="currentPassword" type="password" autocomplete="current-password" />
                </label>
                <label>
                  <span>{{ i18n.t('newPassword') }}</span>
                  <input name="newPassword" type="password" autocomplete="new-password" />
                </label>
                <label>
                  <span>{{ i18n.t('newPasswordAgain') }}</span>
                  <input name="newPasswordAgain" type="password" autocomplete="new-password" />
                </label>
                <button type="button">{{ i18n.t('passwordUpdate') }}</button>
              </form>
            } @else if (section() === 'orders') {
              <div class="placeholder-state">
                <span>☕</span>
                <h2>{{ activeItem().title }}</h2>
                <p>{{ activeItem().description }}</p>
                <a [routerLink]="i18n.routePath('orders')">{{ i18n.t('myOrders') }}</a>
              </div>
            } @else if (section() === 'calls') {
              <app-call-history-widget mode="customer" />
            } @else if (section() === 'collections') {
              <div class="collection-manager">
                <header><div><h2>{{ i18n.collectionT('myCollections') }}</h2><p>{{ i18n.collectionT('manageCollections') }}</p></div><button type="button" (click)="createCollection()">+ {{ i18n.collectionT('newCollection') }}</button></header>
                @for (collection of collections(); track collection.id) {
                  <form class="collection-manage-card" (ngSubmit)="saveCollection(collection)">
                    <label><span>{{ i18n.collectionT('collectionName') }}</span><input [(ngModel)]="collection.name" [name]="'collectionName'+collection.id" required /></label>
                    <label><span>{{ i18n.collectionT('collectionDescription') }}</span><textarea [(ngModel)]="collection.description" [name]="'collectionDescription'+collection.id" rows="2"></textarea></label>
                    <label><span>{{ i18n.collectionT('visibility') }}</span><select [(ngModel)]="collection.shareWith" [name]="'collectionVisibility'+collection.id"><option value="private">{{ i18n.collectionT('private') }}</option><option value="followers">{{ i18n.collectionT('followers') }}</option><option value="everyone">{{ i18n.collectionT('everyone') }}</option></select></label>
                    <small>{{ collection.itemCount || 0 }} {{ i18n.collectionT('productsInCollection') }}</small>
                    <div class="collection-actions"><button type="submit">{{ i18n.collectionT('save') }}</button>@if(collection.shareWith==='everyone'){<button type="button" class="ghost" (click)="shareCollection(collection)">{{ i18n.collectionT('copyLink') }}</button>}<button type="button" class="danger" (click)="deleteCollection(collection)">{{ i18n.collectionT('delete') }}</button></div>
                  </form>
                } @empty { <div class="placeholder-state"><span>♥</span><h2>{{ i18n.collectionT('collectionEmpty') }}</h2></div> }
              </div>
            } @else {
              <div class="account-list">
                @for (item of activeItems(); track trackItem($index, item)) {
                  <a class="list-card" [routerLink]="itemLink(item)">
                    @if (mediaUrl(item.imageUrl)) {
                      <img [src]="mediaUrl(item.imageUrl)" [alt]="itemTitle(item)" loading="lazy" />
                    } @else {
                      <span class="list-fallback">{{ itemTitle(item).charAt(0) || 'F' }}</span>
                    }
                    <div>
                      <strong>{{ itemTitle(item) }}</strong>
                      @if (item.comment) {
                        <p>{{ item.comment }}</p>
                      }
                      @if (item.rating) {
                        <small>{{ item.rating }} / 5 · {{ readableDate(item.createdAt) }}</small>
                      } @else {
                        <small>{{ readableDate(item.createdAt || item.followedAt) }}</small>
                      }
                    </div>
                    <i>{{ i18n.t('openDetail') }}</i>
                  </a>
                } @empty {
                  <div class="placeholder-state">
                    <span>{{ placeholderIcon() }}</span>
                    <h2>{{ i18n.t('noRecordsYet') }}</h2>
                    <p>{{ activeItem().description }}</p>
                  </div>
                }
              </div>
            }
          </article>
        </div>
      }
    </section>
  `,
  styles: [`
    .account-page { display: grid; gap: 18px; margin: 0 auto; max-width: 1160px; }
    .page-title, .account-login-card, .account-shell {
      background: color-mix(in srgb, var(--surface) 78%, transparent);
      border: 1px solid var(--border);
      border-radius: 28px;
      box-shadow: 0 18px 52px color-mix(in srgb, var(--shadow) 30%, transparent);
    }
    .page-title { padding: clamp(20px, 3vw, 30px); }
    .page-title h1 { font-size: clamp(30px, 4vw, 48px); margin-bottom: 8px; }
    .page-title p:last-child, .account-login-card p, .placeholder-state p { color: var(--muted); line-height: 1.55; }
    .account-login-card { align-items: center; display: grid; gap: 22px; grid-template-columns: auto 1fr auto; padding: 24px; }
    .soft-mark, .user-card span, .placeholder-state span, .list-fallback {
      align-items: center;
      background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--ink) 68%, var(--accent)));
      border-radius: 22px;
      color: var(--paper);
      display: inline-flex;
      font-weight: 950;
      height: 58px;
      justify-content: center;
      width: 58px;
    }
    .account-shell { display: grid; grid-template-columns: 290px 1fr; overflow: hidden; }
    .account-sidebar {
      background: color-mix(in srgb, var(--paper) 24%, transparent);
      border-right: 1px solid var(--border);
      display: grid;
      gap: 18px;
      align-content: start;
      padding: 18px;
    }
    .user-card {
      background: color-mix(in srgb, var(--surface) 72%, transparent);
      border: 1px solid var(--border);
      border-radius: 24px;
      display: grid;
      gap: 8px;
      padding: 16px;
    }
    .user-card span { border-radius: 18px; height: 46px; width: 46px; }
    .user-card strong { color: var(--ink); line-height: 1.1; }
    .user-card small, .nav-group p { color: var(--muted); font-size: 12px; font-weight: 850; }
    .nav-group { display: grid; gap: 7px; }
    .nav-group p { letter-spacing: .12em; margin: 0 0 2px; text-transform: uppercase; }
    .nav-group a {
      align-items: center;
      border: 1px solid transparent;
      border-radius: 16px;
      color: var(--ink);
      display: flex;
      font-weight: 900;
      justify-content: space-between;
      padding: 11px 12px;
      text-decoration: none;
    }
    .free-fortune-link {
      align-items: center;
      background: color-mix(in srgb, var(--accent) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--accent) 34%, var(--border));
      border-radius: 16px;
      color: var(--ink);
      display: flex;
      font-weight: 900;
      justify-content: space-between;
      padding: 12px;
      text-decoration: none;
    }
    .free-fortune-link:hover, .free-fortune-link.active { background: color-mix(in srgb, var(--accent) 18%, transparent); }
    .nav-group a:hover, .nav-group a.active { background: color-mix(in srgb, var(--accent) 12%, transparent); border-color: color-mix(in srgb, var(--accent) 22%, var(--border)); }
    .account-content { min-height: 430px; padding: clamp(20px, 3vw, 34px); }
    .account-form { display: grid; gap: 16px; max-width: 620px; }
    .account-form label { display: grid; gap: 8px; }
    .account-form span { color: var(--muted); font-size: 13px; font-weight: 850; }
    .account-form input, .account-form select {
      background: color-mix(in srgb, var(--paper) 74%, var(--surface));
      border: 1px solid var(--border);
      border-radius: 18px;
      color: var(--ink);
      font: inherit;
      outline: 0;
      padding: 14px 16px;
    }
    .account-form button, .placeholder-state button, .placeholder-state a {
      background: var(--ink);
      border: 0;
      border-radius: 999px;
      color: var(--paper);
      cursor: pointer;
      display: inline-flex;
      font: inherit;
      font-weight: 950;
      justify-content: center;
      justify-self: start;
      padding: 13px 18px;
      text-decoration: none;
    }
    .account-form button:disabled { cursor: wait; opacity: .55; }
    .notice { border-radius: 16px; font-weight: 850; margin: 0 0 18px; padding: 12px 14px; }
    .notice.success { background: color-mix(in srgb, #2ecc71 18%, transparent); color: color-mix(in srgb, #2ecc71 58%, var(--ink)); }
    .notice.error { background: color-mix(in srgb, #e74c3c 16%, transparent); color: color-mix(in srgb, #e74c3c 68%, var(--ink)); }
    .placeholder-state { align-content: center; display: grid; gap: 12px; min-height: 320px; place-items: start; }
    .placeholder-state span { font-size: 28px; }
    .placeholder-state h2 { color: var(--ink); font-size: clamp(26px, 3vw, 38px); letter-spacing: -.05em; margin: 0; }
    .account-list { display: grid; gap: 12px; }
    .collection-manager{display:grid;gap:16px}.collection-manager>header{align-items:center;display:flex;gap:16px;justify-content:space-between}.collection-manager h2{margin:0}.collection-manager p{color:var(--muted);margin:5px 0}.collection-manager button,.collection-actions button{background:var(--ink);border:0;border-radius:999px;color:var(--paper);cursor:pointer;font-weight:900;padding:11px 15px}.collection-manage-card{background:color-mix(in srgb,var(--paper) 62%,var(--surface));border:1px solid var(--border);border-radius:22px;display:grid;gap:12px;padding:16px}.collection-manage-card label{display:grid;gap:6px}.collection-manage-card label span,.collection-manage-card small{color:var(--muted);font-size:12px;font-weight:850}.collection-manage-card input,.collection-manage-card textarea,.collection-manage-card select{background:var(--surface);border:1px solid var(--border);border-radius:14px;color:var(--ink);font:inherit;padding:11px}.collection-actions{display:flex;flex-wrap:wrap;gap:8px}.collection-actions .ghost{background:transparent;border:1px solid var(--border);color:var(--ink)}.collection-actions .danger{background:#a62d3d}
    .list-card {
      align-items: center;
      background: color-mix(in srgb, var(--paper) 62%, var(--surface));
      border: 1px solid var(--border);
      border-radius: 22px;
      color: var(--ink);
      display: grid;
      gap: 14px;
      grid-template-columns: 72px 1fr auto;
      padding: 12px;
      text-decoration: none;
    }
    .list-card:hover { border-color: color-mix(in srgb, var(--accent) 38%, var(--border)); transform: translateY(-1px); }
    .list-card img, .list-fallback { border-radius: 18px; height: 72px; object-fit: cover; width: 72px; }
    .list-card strong { display: block; font-weight: 950; }
    .list-card p { color: var(--muted); margin: 4px 0; }
    .list-card small, .list-card i { color: var(--muted); font-style: normal; font-weight: 800; }
    .account-skeleton { display: grid; gap: 14px; }
    .account-skeleton i {
      animation: pulse 1.1s ease-in-out infinite alternate;
      background: color-mix(in srgb, var(--border) 65%, transparent);
      border-radius: 20px;
      display: block;
      height: 72px;
    }
    @keyframes pulse { from { opacity: .45; } to { opacity: 1; } }
    @media (max-width: 820px) {
      .account-login-card, .account-shell { grid-template-columns: 1fr; }
      .account-sidebar { border-bottom: 1px solid var(--border); border-right: 0; display: flex; gap: 12px; overflow-x: auto; padding-bottom: 14px; }
      .user-card { min-width: 220px; }
      .nav-group { min-width: 210px; }
    }
    @media (max-width: 640px) {
      .account-login-card { align-items: stretch; grid-template-columns: 1fr; }
      .list-card { grid-template-columns: 60px 1fr; }
      .list-card i { display: none; }
      .list-card img, .list-fallback { height: 60px; width: 60px; }
    }
  `]
})
export class AccountPage {
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  private readonly api = inject(ApiClientService);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  private readonly site = inject(SiteConfigService);
  private readonly collectionService = inject(FavoriteCollectionService);
  private readonly routeUrl = signal('');

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly notice = signal('');
  protected readonly error = signal('');
  protected readonly profile = signal<AccountProfile | null>(null);
  protected readonly reviews = signal<AccountListItem[]>([]);
  protected readonly history = signal<AccountListItem[]>([]);
  protected readonly following = signal<AccountListItem[]>([]);
  protected readonly collections = signal<UserCollection[]>([]);

  protected readonly personalForm = { name: '', midName: '', surname: '', sex: '', dateOfBirth: '' };
  protected readonly contactForm = { email: '', phone: '', phoneCountry: 'TR' };

  protected readonly navItems = computed<AccountNavItem[]>(() => [
    { key: 'profile', title: this.i18n.t('profileUpdate'), description: this.i18n.t('profileUpdateLead'), routeKey: 'accountProfile', group: this.i18n.t('accountGroupPersonal') },
    { key: 'contact', title: this.i18n.t('contactUpdate'), description: this.i18n.t('contactUpdateLead'), routeKey: 'accountContact', group: this.i18n.t('accountGroupPersonal') },
    { key: 'password', title: this.i18n.t('passwordUpdate'), description: this.i18n.t('passwordUpdateLead'), routeKey: 'accountPassword', group: this.i18n.t('accountGroupSecurity') },
    { key: 'orders', title: this.i18n.t('myOrders'), description: this.i18n.t('myOrdersLead'), routeKey: 'orders', group: this.i18n.t('accountGroupActivity') },
    { key: 'calls', title: 'Aramalarım', description: 'Sesli ve görüntülü görüşmelerin, harcamaların ve cevapsız çağrıların.', routeKey: 'accountCalls', group: this.i18n.t('accountGroupActivity') },
    { key: 'reviews', title: this.i18n.t('myReviews'), description: this.i18n.t('myReviewsLead'), routeKey: 'accountReviews', group: this.i18n.t('accountGroupActivity') },
    { key: 'history', title: this.i18n.t('recentlyViewed'), description: this.i18n.t('recentlyViewedLead'), routeKey: 'accountHistory', group: this.i18n.t('accountGroupActivity') },
    { key: 'following', title: this.i18n.t('following'), description: this.i18n.t('followingLead'), routeKey: 'accountFollowing', group: this.i18n.t('accountGroupActivity') },
    ...(this.site.enabled('collections') ? [{ key: 'collections' as const, title: this.i18n.collectionT('myCollections'), description: this.i18n.collectionT('manageCollections'), routeKey: 'accountCollections', group: this.i18n.t('accountGroupActivity') }] : [])
  ]);
  protected readonly section = computed<AccountSection>(() => this.resolveSection(this.routeUrl()));
  protected readonly activeItem = computed(() => this.navItems().find((item) => item.key === this.section()) ?? this.navItems()[0]);
  protected readonly groupedNav = computed(() => {
    const groups: { name: string; items: AccountNavItem[] }[] = [];
    for (const item of this.navItems()) {
      let group = groups.find((entry) => entry.name === item.group);
      if (!group) {
        group = { name: item.group, items: [] };
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  });

  constructor() {
    this.routeUrl.set(this.router.url);
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.routeUrl.set(event.urlAfterRedirects);
      void this.loadSection();
    });
    this.seo.setPage({
      title: this.i18n.t('accountSeoTitle'),
      description: this.i18n.t('accountSeoDescription'),
      path: this.i18n.routePath('accountProfile')
    });
    void this.auth.refresh().then(() => this.loadSection());
  }

  protected displayName(): string {
    const profile = this.profile();
    const user = this.auth.user();
    const fullName = [profile?.name, profile?.surname].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.userName || profile?.email || user?.email || this.i18n.t('myAccount');
  }

  protected initials(): string {
    return this.displayName().trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || 'FD';
  }

  protected placeholderIcon(): string {
    const icons: Record<AccountSection, string> = {
      profile: '✦',
      contact: '@',
      password: '•',
      orders: '☕',
      calls: '☎',
      reviews: '★',
      history: '◷',
      following: '♥'
      ,collections: '♦'
    };
    return icons[this.section()];
  }

  protected activeItems(): AccountListItem[] {
    if (this.section() === 'reviews') {
      return this.reviews();
    }
    if (this.section() === 'history') {
      return this.history();
    }
    if (this.section() === 'following') {
      return this.following();
    }
    return [];
  }

  protected trackItem(index: number, item: AccountListItem): string | number {
    return item.id ?? item.sellerID ?? item.listingID ?? index;
  }

  protected itemTitle(item: AccountListItem): string {
    return item.listingTitle || item.title || item.sellerName || item.shopName || item.userName || this.activeItem().title;
  }

  protected itemLink(item: AccountListItem): string {
    if (this.section() === 'following') {
      return this.i18n.routePath('seller', { slug: this.slugify(item.slug || item.shopName || String(item.sellerID || '')) });
    }
    return this.i18n.routePath('listing', { slug: item.listingSlug || item.slug || String(item.listingID || '') });
  }

  protected mediaUrl(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    const clean = String(url).replace('[size]', 'b');
    if (/^https?:\/\//.test(clean)) {
      return clean;
    }
    return `https://www.aymudo.com/${clean.replace(/^\/+/, '')}`;
  }

  protected readableDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return formatLocalDate(value, { day: '2-digit', month: 'short', year: 'numeric' }, this.i18n.lang());
  }

  protected async saveProfile(): Promise<void> {
    this.saving.set(true);
    this.notice.set('');
    this.error.set('');
    try {
      const response = await firstValueFrom(this.api.post<ApiResponse<{ profile: AccountProfile }>>('accountProfileUpdate', this.personalForm));
      if (!response?.success) {
        throw new Error(response?.message || 'Profile update failed.');
      }
      this.setProfile(response.data?.profile || null);
      this.notice.set(this.i18n.t('saved'));
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : this.i18n.t('authError'));
    } finally {
      this.saving.set(false);
    }
  }

  protected async saveContact(): Promise<void> {
    this.saving.set(true);
    this.notice.set('');
    this.error.set('');
    try {
      const response = await firstValueFrom(this.api.post<ApiResponse<{ profile: AccountProfile }>>('accountContactUpdate', this.contactForm));
      if (!response?.success) {
        throw new Error(response?.message || 'Contact update failed.');
      }
      this.setProfile(response.data?.profile || null);
      this.notice.set(this.i18n.t('saved'));
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : this.i18n.t('authError'));
    } finally {
      this.saving.set(false);
    }
  }

  private async loadSection(): Promise<void> {
    if (!this.auth.loggedIn()) {
      return;
    }
    this.loading.set(true);
    this.notice.set('');
    this.error.set('');
    try {
      await this.loadProfile();
      if (this.section() === 'reviews') {
        this.reviews.set(await this.loadItems('accountReviews'));
      } else if (this.section() === 'history') {
        this.history.set(await this.loadItems('accountHistory'));
      } else if (this.section() === 'following') {
        this.following.set(await this.loadItems('accountFollowing'));
      } else if (this.section() === 'collections') {
        this.collections.set(await this.collectionService.collections());
      }
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : this.i18n.t('authError'));
    } finally {
      this.loading.set(false);
    }
  }

  private async loadProfile(): Promise<void> {
    const response = await firstValueFrom(this.api.post<ApiResponse<{ profile: AccountProfile }>>('accountProfile', {}));
    if (!response?.success) {
      throw new Error(response?.message || 'Profile could not be loaded.');
    }
    this.setProfile(response.data?.profile || null);
  }

  private async loadItems(apiKey: string): Promise<AccountListItem[]> {
    const response = await firstValueFrom(this.api.post<ApiResponse<{ items: AccountListItem[] }>>(apiKey, { limit: 50 }));
    if (!response?.success) {
      throw new Error(response?.message || 'List could not be loaded.');
    }
    return response.data?.items || [];
  }

  private setProfile(profile: AccountProfile | null): void {
    if (!profile) {
      return;
    }
    this.profile.set(profile);
    this.personalForm.name = profile.name || '';
    this.personalForm.midName = profile.midName || '';
    this.personalForm.surname = profile.surname || '';
    this.personalForm.sex = profile.sex || '';
    this.personalForm.dateOfBirth = profile.dateOfBirth || '';
    this.contactForm.email = profile.email || this.auth.user()?.email || '';
    this.contactForm.phone = profile.phone || '';
    this.contactForm.phoneCountry = profile.phoneCountry || 'TR';
  }

  private resolveSection(url: string): AccountSection {
    const cleanUrl = url.split('?')[0].split('#')[0];
    if (cleanUrl.includes('iletisim') || cleanUrl.includes('contact')) {
      return 'contact';
    }
    if (cleanUrl.includes('sifre') || cleanUrl.includes('password')) {
      return 'password';
    }
    if (cleanUrl.includes('fallarim') || cleanUrl.includes('siparis') || cleanUrl.includes('orders') || cleanUrl.includes('readings')) {
      return 'orders';
    }
    if (cleanUrl.includes('aramalarim') || cleanUrl.includes('/calls')) {
      return 'calls';
    }
    if (cleanUrl.includes('yorum') || cleanUrl.includes('review')) {
      return 'reviews';
    }
    if (cleanUrl.includes('gezdik') || cleanUrl.includes('history')) {
      return 'history';
    }
    if (cleanUrl.includes('takip') || cleanUrl.includes('following')) {
      return 'following';
    }
    if (cleanUrl.includes('koleksiyon') || cleanUrl.includes('collections')) return 'collections';
    return 'profile';
  }

  protected async createCollection(): Promise<void> {
    const created=await this.collectionService.createCollection(this.i18n.collectionT('newCollection'));
    if(created) this.collections.update(items=>[created,...items]);
  }

  protected async saveCollection(collection: UserCollection): Promise<void> {
    await this.collectionService.updateCollection(collection);
    this.notice.set(this.i18n.t('saved'));
  }

  protected async deleteCollection(collection: UserCollection): Promise<void> {
    await this.collectionService.deleteCollection(collection.id);
    this.collections.update(items=>items.filter(item=>item.id!==collection.id));
  }

  protected async shareCollection(collection: UserCollection): Promise<void> {
    const path=this.i18n.routePath('collection',{id:collection.id,slug:collection.slug || String(collection.id)});
    const url=typeof window!=='undefined' ? new URL(path,window.location.origin).toString() : path;
    if(typeof navigator!=='undefined' && navigator.clipboard) await navigator.clipboard.writeText(url);
    this.notice.set(this.i18n.collectionT('copyLink'));
  }

  private slugify(value: string): string {
    return value
      .toLocaleLowerCase('tr')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
